import { warnDeprecatedPrivacyCenterSlug } from "../shared/endpoints";
import type { PrivacyCenterHandle, PrivacyCenterOptions } from "./types";

/** Where the hosted Privacy Center lives in production. */
export const DEFAULT_PRIVACY_CENTER_ORIGIN = "https://console.redacto.io";

/** The message the hosted page posts with its content height, on load and on resize. */
export const PRIVACY_CENTER_HEIGHT_MESSAGE = "redacto:privacy-center:height";

const DEFAULT_HEIGHT = "720px";
const DEFAULT_TITLE = "Privacy Center";

const toCssLength = (value: number | string): string =>
  typeof value === "number" ? `${value}px` : value;

const resolveTarget = (target: string | HTMLElement): HTMLElement => {
  if (typeof target !== "string") {
    return target;
  }
  const element = document.querySelector<HTMLElement>(target);
  if (!element) {
    throw new Error(`Target element not found: ${target}`);
  }
  return element;
};

/**
 * The page to frame: `url` as given, else the hosted sign-in for the
 * organisation (or, deprecated, the slug) on `origin`. `language` becomes
 * `?lang=` either way.
 */
export const privacyCenterUrl = (options: PrivacyCenterOptions): string => {
  const base = options.url
    ? options.url
    : `${(options.origin ?? DEFAULT_PRIVACY_CENTER_ORIGIN).replace(/\/+$/, "")}/consent/privacy-center/${encodeURIComponent(
        options.organisationUuid || options.slug || "",
      )}/sign-in`;
  const url = new URL(base, window.location.href);
  if (options.language) {
    url.searchParams.set("lang", options.language);
  }
  return url.toString();
};

const readHeight = (data: unknown): number | null => {
  if (typeof data !== "object" || data === null) {
    return null;
  }
  const message = data as { type?: unknown; height?: unknown };
  if (message.type !== PRIVACY_CENTER_HEIGHT_MESSAGE) {
    return null;
  }
  const height = message.height;
  return typeof height === "number" && Number.isFinite(height) && height > 0
    ? Math.ceil(height)
    : null;
};

/**
 * Mounts the hosted Privacy Center in an iframe inside `target`. The frame
 * grows to fit its content when the hosted page reports its height; with
 * `height` set it keeps that height instead. Returns a handle whose
 * `destroy()` removes the frame and its listener.
 */
export const mountPrivacyCenter = (options: PrivacyCenterOptions): PrivacyCenterHandle => {
  if (!options.url && !options.organisationUuid && !options.slug) {
    throw new Error("privacyCenter() needs an organisationUuid or a url");
  }
  warnDeprecatedPrivacyCenterSlug(options.slug);
  const container = resolveTarget(options.target);
  const src = privacyCenterUrl(options);
  const fixedHeight = options.height !== undefined && options.height !== "auto";

  const iframe = document.createElement("iframe");
  iframe.src = src;
  iframe.title = options.title ?? DEFAULT_TITLE;
  iframe.setAttribute("data-redacto-privacy-center", "");
  iframe.setAttribute("allow", "clipboard-write");
  iframe.style.display = "block";
  iframe.style.width = "100%";
  iframe.style.border = "0";
  iframe.style.height = fixedHeight ? toCssLength(options.height as number | string) : DEFAULT_HEIGHT;

  // Only the frame this call created may resize it; its payload is a number.
  const onMessage = (event: MessageEvent) => {
    if (fixedHeight || event.source !== iframe.contentWindow) {
      return;
    }
    const height = readHeight(event.data);
    if (height !== null) {
      iframe.style.height = `${height}px`;
    }
  };

  window.addEventListener("message", onMessage);
  container.appendChild(iframe);

  let destroyed = false;
  return {
    iframe,
    destroy: () => {
      if (destroyed) {
        return;
      }
      destroyed = true;
      window.removeEventListener("message", onMessage);
      iframe.remove();
    },
  };
};
